import styled, { css } from 'styled-components';
import React from 'react';

/**
 * Интерфейс пропсов для стилизованного div-компонента
 * Префикс $ используется для пропсов, которые передаются только в styled-components
 * и не попадают в DOM-элемент (best practice для styled-components)
 */
interface ContainerDivProps {
  /**
   * Включение полноэкранного режима - контейнер занимает 100% высоты viewport
   * @default false
   */
  $isFullScreen?: boolean;

  /**
   * Фиксированное позиционирование - контейнер фиксируется поверх всего контента
   * Автоматически включает затемнение фона, если не указан кастомный $background
   * @default false
   */
  $isFixed?: boolean;

  /**
   * z-index для управления порядком наложения элементов
   * Особенно полезно при использовании $isFixed
   * @default 9999 для фиксированного режима
   */
  $zIndex?: number;

  /**
   * Кастомный фон контейнера. Переопределяет стандартное поведение.
   * Может принимать любые CSS-значения: цвета, градиенты, изображения
   * @example "#ffffff", "rgba(0,0,0,0.5)", "url('bg.jpg')", "linear-gradient(...)"
   */
  $background?: string;
}

/**
 * Стилизованный div-компонент с условными стилями.
 * Использует шаблонные литералы для динамического применения стилей
 */
const ContainerDiv = styled.div<ContainerDivProps>`
    /* Базовые стили flex-контейнера с центрированием */
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    /* 
     * Условные стили на основе пропсов
     * Используется функция-интерполяция для доступа к пропсам
     */
    ${({ $isFullScreen, $isFixed, $zIndex }) => {
  // Приоритет №1: фиксированное позиционирование
  if ($isFixed) {
    return css`
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: ${$zIndex || 9999}; /* Дефолтный высокий z-index */
            `;
  }

  // Приоритет №2: полноэкранный режим без фиксации
  if ($isFullScreen) {
    return css`
                height: 100vh;    /* 100% высоты viewport */
                width: 100%;      /* 100% ширины */
            `;
  }

  // Дефолтные стили (обычный контейнер)
  return css`
            width: 100%; /* Занимает всю доступную ширину */
        `;
}};

    /* 
     * Логика определения фона с приоритетами:
     * 1. Если передан $background - используем его
     * 2. Если нет $background и $isFixed - полупрозрачный черный
     * 3. Во всех остальных случаях - прозрачный
     */
    background: ${({ $isFixed, $background }) => {
  if ($background) {
    return $background; // Кастомный фон имеет высший приоритет
  }
  return $isFixed ? 'rgba(0, 0, 0, 0.5)' : 'transparent';
}};
`;

/**
 * Основные пропсы компонента Container
 * Наследует все пропсы от ContainerDivProps + children и className
 */
interface Props extends ContainerDivProps {
  /**
   * Дочерние элементы компонента
   * @required
   */
  children: React.ReactNode;

  /**
   * Дополнительные CSS-классы для кастомизации
   * @optional
   */
  className?: string;
}

/**
 * Универсальный контейнерный компонент с гибкими режимами отображения
 *
 * @component
 * @example
 * // Простой контейнер
 * <Container>Контент</Container>
 *
 * // Полноэкранный контейнер
 * <Container $isFullScreen>На весь экран</Container>
 *
 * // Фиксированный модальный контейнер
 * <Container $isFixed $zIndex={10000}>Поверх всего</Container>
 *
 * // Контейнер установлен кастомный фоном
 * <Container $background="linear-gradient(blue, red)">С градиентом</Container>
 */
export const Container: React.FC<Props> = (props) => {
  const {
    children,
    $isFullScreen = false,
    $isFixed = false,
    $zIndex,
    $background,
    className,
  } = props;

  return (
    <ContainerDiv
      $isFullScreen={$isFullScreen}
      $isFixed={$isFixed}
      $zIndex={$zIndex}
      $background={$background}
      className={className}
    >
      {children}
    </ContainerDiv>
  );
};
